// Default opacity for newly committed visible layers when no better source exists.
export const DEFAULT_LAYER_OPACITY = 0.75;

// Delay before clearing the map when no layer currently matches filters.
// Increase to reduce blank flashes during rapid filter updates.
// Decrease to clear faster when the no-layer state is truly final.
export const NO_LAYER_CLEAR_DELAY_MS = 250;

// Debounce for `_syncShownLayer` to avoid request storms while scrubbing sliders.
// Increase to reduce network churn; decrease for faster reaction to user input.
export const SYNC_DEBOUNCE_MS = 80;

// Earliest moment after commit when old layers may be removed.
// Increase for smoother hand-off; decrease to reduce overlap time.
export const CLEANUP_MIN_DELAY_MS = 120;

// Poll interval while waiting for export traffic to settle.
// Lower values react faster but poll more often.
export const CLEANUP_POLL_DELAY_MS = 60;

// Maximum time to keep old layers before forced cleanup.
// Increase for slow networks/services; decrease to avoid prolonged double-render.
export const CLEANUP_MAX_DELAY_MS = 420;

// Opacity of old layers during overlap after commit.
// Lower values reduce ghosting; higher values reduce perceived popping.
export const OVERLAP_DIM_OPACITY = 0.15;

// Hard timeout for a single transition before fallback commit.
// Increase to wait longer on slow backends; decrease to fail over sooner.
export const TRANSITION_TIMEOUT_MS = 12000;

// Short wait after `whenLayerView` resolves to catch delayed updating=true.
// Increase if first uncached loads finalize too early; decrease for snappier commit.
export const LAYERVIEW_SETTLE_FALLBACK_MS = 700;

const isConstraintValueMatch = ({ requiredValue, realValue }) => {
  if (realValue === undefined || requiredValue === undefined) return false;
  return (
    String(realValue).toUpperCase() === String(requiredValue).toUpperCase()
  );
};

export const normalizeLayerOpacity = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (numeric < 0) return 0;
  if (numeric > 1) return 1;
  return numeric;
};

export const findPositiveOnMapOpacity = ({
  layers = {},
  map,
  isLayerOnMap,
  excludeInternalId = null,
}) => {
  let result = null;
  Object.entries(layers).some(([key, layer]) => {
    const internalId = Number(key);
    if (excludeInternalId !== null && internalId === excludeInternalId) {
      return false;
    }
    if (!layer || !isLayerOnMap(map, layer)) {
      return false;
    }

    const opacity = normalizeLayerOpacity(layer.opacity);
    if (opacity !== null && opacity > 0) {
      result = { internalId, opacity };
      return true;
    }
    return false;
  });
  return result;
};

export const resolveTargetLayerOpacity = ({
  layers = {},
  currentVisibleLayerId = null,
  operationalOpacity,
  map,
  isLayerOnMap,
  lastKnownOperationalOpacity = null,
  defaultOpacity = DEFAULT_LAYER_OPACITY,
}) => {
  const currentLayer =
    currentVisibleLayerId !== null ? layers[currentVisibleLayerId] : null;
  const currentLayerOpacity = normalizeLayerOpacity(currentLayer?.opacity);

  const storedOpacity = normalizeLayerOpacity(operationalOpacity);
  if (storedOpacity !== null) {
    // A transient 0 can happen mid-transition; try to recover a visible
    // opacity first so layer switches do not snap to default opacity.
    if (storedOpacity === 0) {
      const onMapFallback = findPositiveOnMapOpacity({
        layers,
        map,
        isLayerOnMap,
      });
      if (onMapFallback) {
        return onMapFallback.opacity;
      }

      const rememberedOpacity = normalizeLayerOpacity(
        lastKnownOperationalOpacity
      );
      if (rememberedOpacity !== null && rememberedOpacity > 0) {
        return rememberedOpacity;
      }
    }

    if (storedOpacity > 0) {
      return storedOpacity;
    }
  }

  if (currentLayerOpacity !== null && currentLayerOpacity > 0) {
    return currentLayerOpacity;
  }

  const fallback = findPositiveOnMapOpacity({
    layers,
    map,
    isLayerOnMap,
    excludeInternalId: currentVisibleLayerId,
  });
  if (fallback) {
    return fallback.opacity;
  }

  const rememberedOpacity = normalizeLayerOpacity(lastKnownOperationalOpacity);
  if (rememberedOpacity !== null && rememberedOpacity > 0) {
    return rememberedOpacity;
  }

  return defaultOpacity;
};

export const deactivateLayerWrapper = ({
  layer,
  map,
  isLayerOnMap,
  removeFromMap,
}) => {
  if (!layer) return;
  layer.setOpacity(0);
  if (isLayerOnMap(map, layer)) {
    removeFromMap(layer, map);
  }
};

export const deactivateAllLayersExcept = ({
  layers = {},
  keepId,
  map,
  isLayerOnMap,
  removeFromMap,
}) => {
  Object.entries(layers).forEach(([key, layer]) => {
    if (Number(key) === keepId) return;
    deactivateLayerWrapper({
      layer,
      map,
      isLayerOnMap,
      removeFromMap,
    });
  });
};

export const dimAllLayersExcept = ({
  layers = {},
  keepId,
  map,
  isLayerOnMap,
  maxOpacity = OVERLAP_DIM_OPACITY,
}) => {
  Object.entries(layers).forEach(([key, layer]) => {
    if (Number(key) === keepId) return;
    if (!layer || !isLayerOnMap(map, layer)) return;

    const currentOpacity = Number(layer.opacity);
    const nextOpacity = Number.isFinite(currentOpacity)
      ? Math.min(currentOpacity, maxOpacity)
      : maxOpacity;

    if (currentOpacity !== nextOpacity) {
      layer.setOpacity(nextOpacity);
    }
  });
};

export const hasOtherManagedMapLayers = ({
  layers = {},
  map,
  isLayerOnMap,
  excludeInternalId,
}) =>
  Object.entries(layers).some(
    ([key, layer]) =>
      Number(key) !== excludeInternalId && layer && isLayerOnMap(map, layer)
  );

export const buildShownLayerInfos = ({ layerIds = [], layers = {} }) =>
  layerIds.map((info) => ({
    layerId: info.id,
    layer: layers[info._internalID],
    metaData: info,
  }));

export const autoSelectSingleChoiceForZeitabschnitt = ({
  attributeId,
  items,
  attributeStore,
  parameterStore,
}) => {
  if (attributeId !== "Zeitabschnitt") return false;
  if (!Array.isArray(items) || items.length !== 1) return false;
  if (attributeStore?.value !== undefined && attributeStore?.value !== null) {
    return false;
  }

  const parameterIsSet =
    parameterStore?.value !== undefined && parameterStore?.value !== null;
  if (!parameterIsSet) return false;

  const onlyChoice = items[0];
  if (
    onlyChoice &&
    onlyChoice.value !== undefined &&
    String(onlyChoice.label).trim().toLowerCase() !== "skip"
  ) {
    attributeStore.setValue(onlyChoice.value);
    return true;
  }

  return false;
};

export const isSet = (value) => value !== undefined && value !== null;

export const isSameValue = (left, right) => {
  if (left === right) return true;
  if (!isSet(left) || !isSet(right)) return false;
  return String(left) === String(right);
};

export const findChoiceByValue = (choices = [], value) =>
  choices.find((c) => isSameValue(c?.value, value));

/**
 * Attempts to restore the Zeitabschnitt value by matching a previously cached
 * label against the current available choices.
 *
 * Called after a parameter change causes the Zeitabschnitt value to be reset
 * (because the new choices carry different integer offsets). If the same label
 * still exists in the new choice set, the value is restored transparently.
 *
 * @param {object} params
 * @param {object} params.zeitabschnittStore - AttributeStore for "Zeitabschnitt"
 * @param {string|null} params.savedLabel - Label to look up in current choices
 * @returns {boolean} true if a match was found and value was restored
 */
export const preserveZeitabschnittByLabel = ({
  zeitabschnittStore,
  savedLabel,
}) => {
  if (!savedLabel || !zeitabschnittStore) return false;
  const choices = zeitabschnittStore.updateOptions?.choices ?? [];
  const normalizedSavedLabel = String(savedLabel).trim().toLowerCase();
  const match = choices.find(
    (c) => String(c?.label ?? "").trim().toLowerCase() === normalizedSavedLabel
  );
  if (match) {
    zeitabschnittStore.setValue(match.value);
    return true;
  }
  return false;
};

export const shouldShowHiddenAttributeByStores = ({
  attributeId,
  choices,
  filterLevel,
  hideAsDropdown,
  getAttributeStore,
}) => {
  if (
    !hideAsDropdown ||
    !Array.isArray(hideAsDropdown) ||
    !hideAsDropdown.includes(attributeId) ||
    !Array.isArray(choices) ||
    choices.length === 0
  ) {
    return null;
  }

  const levelIndex = filterLevel.indexOf(attributeId);
  if (levelIndex <= 0) return null;

  const isPrevSatisfied = (prevLevel) => {
    const prevStore = getAttributeStore(prevLevel);
    if (!prevStore) return false;

    // 1) Explicit value set
    if (prevStore.value !== undefined && prevStore.value !== null) {
      return true;
    }

    // 2) All available choices are 'skip'
    const prevChoices = Array.isArray(prevStore.updateOptions?.choices)
      ? prevStore.updateOptions.choices
      : [];
    if (prevChoices.length > 0) {
      const onlySkip = prevChoices.every(
        (c) => ((c.label || "") + "").toString().trim().toLowerCase() === "skip"
      );
      if (onlySkip) return true;
    }

    // 3) Previous level itself is hidden-as-dropdown
    if (hideAsDropdown.includes(prevLevel)) return true;

    return false;
  };

  return filterLevel.slice(0, levelIndex).every(isPrevSatisfied);
};

export const layerMatchesConstraints = ({
  layer,
  filterLevel = [],
  filterValues = [],
}) => {
  const constraints = layer?.constraints || {};
  return Object.keys(constraints).every((attribute) => {
    const realValue = filterValues[filterLevel.indexOf(attribute)]?.label;
    const requiredValue = constraints[attribute];
    return isConstraintValueMatch({ requiredValue, realValue });
  });
};

export const layerMatchesConstraintsExcept = ({
  layer,
  filterLevel = [],
  filterValues = [],
  excluded = {},
  defaultUrl,
}) => {
  const constraints = layer?.constraints || {};
  const excludedAttributes = Object.keys(excluded || {});
  const constraintAttributes = Object.keys(constraints);

  const constraintsExcept = constraintAttributes.filter(
    (attribute) => !excludedAttributes.includes(attribute)
  );

  const matches = constraintsExcept.every((attribute) => {
    const realValue = filterValues[filterLevel.indexOf(attribute)]?.label;
    const requiredValue = constraints[attribute];
    return isConstraintValueMatch({ requiredValue, realValue });
  });

  if (!matches) return false;

  const clonedLayer = JSON.parse(JSON.stringify(layer));
  clonedLayer.possibilities = {};

  excludedAttributes.forEach((attribute) => {
    const candidateValues = Array.isArray(excluded[attribute])
      ? excluded[attribute]
      : [];
    clonedLayer.possibilities[attribute] = candidateValues.filter((exVal) =>
      isConstraintValueMatch({
        realValue: exVal,
        requiredValue: constraints[attribute],
      })
    );
  });

  if (defaultUrl) {
    clonedLayer.url = layer?.url || defaultUrl;
  }

  return clonedLayer;
};

export const resolveLayerSource = ({ baseUrl, id, url }) => {
  let layerBaseUrl = url || baseUrl;
  let layerId = id;

  if (url) {
    const matches = url.match(/(.*\/)(\d*)\/*$/);
    if (matches) {
      layerBaseUrl = matches[1].replace(/\/+$/, "");
      layerId = matches[2];
    }
  }

  return { layerBaseUrl, layerId };
};

export const hasUnresolvedFilterValues = (filterValues = []) =>
  filterValues.some(
    (item) => item?.value === undefined || item?.value === null
  );
